import { useEffect, useState } from "react";
import { AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, inr, today } from "@/lib/api";
import { TableWrap, Empty, th, td } from "@/components/common";
import { downloadCsv } from "@/components/market";

export function FleetRanking({ onOpen }) {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/central-expenses/fleet-ranking", { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [month]);
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const csv = () => downloadCsv(`Fleet_Profit_Ranking_${month}.csv`, ["Rank", "Vehicle", "Trips", "Earnings", "Total Cost", "Net Profit", "Margin %"],
    d.rows.map((r) => [r.rank, r.vehicle_number, r.trips, r.earnings, r.total_cost, r.net, r.margin ?? ""]));
  return (
    <div data-testid="fleet-ranking">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input type="month" className="w-44" data-testid="ranking-month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        {d.loss_count > 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-400" data-testid="ranking-loss-alert">
            <AlertTriangle className="h-4 w-4" />{d.loss_count} gaadi ghaate me
          </div>
        ) : <span className="text-sm font-semibold text-emerald-600" data-testid="ranking-no-loss">No loss-making vehicle this month</span>}
        <span className="text-sm text-muted-foreground">Fleet net: <b className={`amount ${d.total_net >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="ranking-total-net">{inr(d.total_net)}</b></span>
        <Button size="sm" variant="outline" className="ml-auto" data-testid="ranking-csv-btn" onClick={csv}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button>
      </div>
      <TableWrap>
        <thead><tr>{["#", "Vehicle", "Net Profit", "Margin", "Earnings", "Total Cost", "Trips"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {d.rows.map((r) => (
            <tr key={r.vehicle_id} onClick={() => onOpen({ id: r.vehicle_id, vehicle_number: r.vehicle_number })} data-testid={`ranking-row-${r.vehicle_number}`}
              className={`cursor-pointer border-t transition-colors hover:bg-secondary/60 ${r.net < 0 ? "bg-rose-50/70 dark:bg-rose-950/20" : ""}`}>
              <td className={`${td} font-bold`}>{r.rank}</td>
              <td className={`${td} font-mono font-semibold`}>{r.vehicle_number}</td>
              <td className={`${td} amount font-bold ${r.net < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`ranking-net-${r.vehicle_number}`}>{inr(r.net)}</td>
              <td className={`${td} text-xs`}>{r.margin === null ? "-" : `${r.margin}%`}</td>
              <td className={`${td} amount`}>{inr(r.earnings)}</td>
              <td className={`${td} amount`}>{inr(r.total_cost)}</td>
              <td className={td}>{r.trips}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!d.rows.length && <Empty testid="ranking-empty" text="No vehicles" />}
    </div>
  );
}
